import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";
import { getLoggedinUser } from "helpers/api_helper";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import { Autocomplete, TextField } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import PageContainer from "components/PageContainer/PageContainer";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import { showError, showConfirm } from "utils/swalHelper";
import { useAutoMinimizeSidebar } from "CustomHooks/useAutoMinimizeSidebar";

interface GRNFormValues {
  inward_date: string;
  customer: any;
  order_no: string;
  dispatch_no: string;
  remark: string;
  upload_document: File | null;
  grn_items: any;
  received_by: string;
  received_by_user_id: string;
}

const SalesOrderReturnGNRForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { dispatch_id } = useParams();
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [dispatchData, setDispatchData] = useState<any>({});
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);

  const validationSchema = Yup.object({
    inward_date: Yup.string().required("Please select Inward Date"),
    customer: Yup.object().nullable().required("Please enter Customer Name"),
    order_no: Yup.string().required("Please enter Order No"),
    dispatch_no: Yup.string().required("Please enter Dispatch No"),
    remark: Yup.string(), // optional
    upload_document: Yup.mixed()
      .nullable()
      .test("fileSize", "File size too large (max 2MB)", (file: any) => {
        if (!file) return true; // skip if empty
        return file.size <= 2 * 1024 * 1024;
      }),
    received_by_user_id: Yup.string().required("Please select Received By"),
  });

  // Auto-minimize sidebar on tablet screens
  useAutoMinimizeSidebar();

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };
  const formik = useFormik<GRNFormValues>({
    initialValues: {
      inward_date: getTodayDate(),
      customer: "",
      order_no: "",
      dispatch_no: "",
      remark: "",
      upload_document: null,
      grn_items: [],
      received_by: "",
      received_by_user_id: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!values.grn_items || values.grn_items.length === 0) {
        showError("At least one item is required.");
        return;
      }

      // Validate that all items have return_quantity greater than zero
      const itemsWithInvalidReturnQuantity = values.grn_items.filter(
        (item: any) => {
          const returnQty = parseFloat(item?.return_quantity) || 0;
          return returnQty <= 0;
        }
      );

      if (itemsWithInvalidReturnQuantity?.length > 0) {
        toast.error("All items must have return quantity greater than zero");
        return;
      }

      // Validate that return quantity is less than or equal to dispatched quantity
      const itemsWithExcessiveReturnQuantity = values.grn_items.filter(
        (item: any) => {
          const returnQty = parseFloat(item?.return_quantity) || 0;
          const dispatchQty = parseFloat(item?.dispatch_quantity) || 0;
          return returnQty > dispatchQty;
        }
      );

      if (itemsWithExcessiveReturnQuantity?.length > 0) {
        toast.error(
          "Return quantity cannot be greater than dispatched quantity"
        );
        return;
      }

      const formData = new FormData();
      formData.append("inward_type", "Order Return");
      formData.append("inward_date", values.inward_date);
      formData.append("customer_id", values.customer?.customer_id);
      formData.append("order_id", dispatchData?.order_id);
      formData.append("dispatch_id", dispatchData?.dispatch_id);
      formData.append("warehouse_id", dispatchData?.issued_from_warehouse_id);
      formData.append("remark", values.remark);
      formData.append("received_by", values.received_by_user_id || "");
      formData.append(
        "supplier_details",
        JSON.stringify(dispatchData?.customer_details) || ""
      );

      // Append file if it exists (must be File object, not string)
      if (values.upload_document && values.upload_document instanceof File) {
        formData.append("document_file", values.upload_document);
      }
      const updated_items = values?.grn_items?.map((item: any) => ({
        master_item_id: item?.master_item_id || item?.item_details?.item_id,
        selling_price: parseFloat(item?.rate),
        quantity: parseFloat(item?.return_quantity),
        reserved_quantity: 0,
        available_quantity: parseFloat(item?.return_quantity),
        issued_quantity: parseFloat(item?.dispatch_quantity),
        remark: item?.remark,
        warehouse_id: dispatchData?.issued_from_warehouse_id,
      }));
      formData.append("grn_items", JSON.stringify(updated_items) || "[]");
      console.log("Payload : ", updated_items);

      try {
        await Axios.post(
          `${api?.warehouse?.item_inward?.sales_item_inword_create}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        toast.success("GRN created successfully");
        navigate("/warehouse-item-inward");
      } catch (err: any) {
        const backendMessage = err?.response?.data?.message;
        const errType = err?.response?.data?.errors;
        if (typeof backendMessage === "string") {
          toast.error(backendMessage);
        } else if (errType && Object.keys(errType).length > 0) {
          Object.keys(errType).forEach((key) => {
            showError(errType[key][0]);
          });
        } else {
          toast.error("Failed to create GRN");
          console.error("Error creating GRN:", err);
        }
      }
    },
  });

  // Helper function to allow only numbers
  const handleNumberInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    const regex = /^[0-9]*\.?[0-9]*$/;
    if (value === "" || regex.test(value)) {
      return value;
    }
    return false;
  };

  // Handler to update grn_items array fields
  const handleItemFieldChange = (index: number, field: string, value: any) => {
    const updatedItems = [...(formik.values.grn_items || [])];
    if (field === "return_quantity") {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
        amount: parseFloat(updatedItems[index]?.rate) * Number(value),
      };
    } else if (field === "rate") {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
        amount:
          parseFloat(updatedItems[index]?.return_quantity) * Number(value),
      };
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
      };
    }
    formik.setFieldValue("grn_items", updatedItems);
  };

  // Handler to calculate amount when rate or return_quantity changes
  const handleAmountCalculation = (
    index: number,
    returnQty: number,
    rate: number
  ) => {
    const amount = (returnQty || 0) * (rate || 0);
    handleItemFieldChange(index, "amount", amount);
  };

  useEffect(() => {
    const fetchUserOptions = async () => {
      try {
        const response = await Axios.get("/users/dropdown/all");
        setUserOptions(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching user options:", error);
        setUserOptions([]);
      }
    };
    fetchUserOptions();
  }, []);

  // Auto-select current logged-in user for received_by
  useEffect(() => {
    if (userOptions.length > 0 && !formik.values.received_by_user_id) {
      const loggedInUser = getLoggedinUser();
      if (loggedInUser?.user_id) {
        // Find the logged-in user in the userOptions array
        const currentUser = userOptions.find(
          (opt: any) => opt.user_id === loggedInUser.user_id
        );

        if (currentUser) {
          formik.setFieldValue("received_by_user_id", currentUser.user_id);
          const fullName =
            currentUser.first_name && currentUser.last_name
              ? `${currentUser.first_name} ${currentUser.last_name}`
              : currentUser.username || currentUser.email || "";
          formik.setFieldValue("received_by", fullName);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOptions]);

  useEffect(() => {
    if (!dispatch_id) return;

    setIsLoadingInitialData(true);
    Axios.get(
      `${api?.warehouse?.item_inward?.sales_order_return_dispatch_by_id}/${dispatch_id}`
    )
      .then((result) => {
        console.log("Sales order return data : ", result?.data?.data);
        formik?.setFieldValue(
          "customer",
          result?.data?.data?.customer_details || []
        );
        const updatedItems = result?.data?.data?.dispatch_items?.map(
          (item: any) => ({
            ...item,
            return_quantity: item?.return_quantity || item?.dispatch_quantity || 0,
            order_quantity: item?.total_quantity || item?.order_quantity,
          })
        );
        formik?.setFieldValue("grn_items", updatedItems || []);
        formik?.setFieldValue("order_no", result?.data?.data?.order_no || "");
        formik?.setFieldValue(
          "dispatch_no",
          result?.data?.data?.dispatch_number || ""
        );
        setDispatchData(result?.data?.data || {});
      })
      .catch((err) => {
        toast.error("failed to fetch sales order return item data");
      })
      .finally(() => {
        setIsLoadingInitialData(false);
      });
  }, [dispatch_id]);

  return (
    <PageContainer>
      <FormHeader
        title="Create GRN (Sales Order Return)"
        firstLabel="Sales Order Return"
        secondLabel="Create GRN"
        handleArrowClick={() => navigate("/warehouse-item-inward")}
      />
      {isLoadingInitialData ? (
        <LoadingSpinner />
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>
                  Inward Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="inward_date"
                  value={formik.values.inward_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.inward_date && formik.touched.inward_date
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.inward_date}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>
                  Customer Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="customer"
                  placeholder="Customer Name"
                  value={formik.values?.customer?.customer_name || ""}
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched("customer", true)}
                  isInvalid={
                    !!formik.errors.customer && !!formik.touched.customer
                  }
                  readOnly
                />
                <Form.Control.Feedback type="invalid">
                  {typeof formik.errors.customer === "string"
                    ? formik.errors.customer
                    : "Please enter Customer Name"}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>
                  Order No. <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="order_no"
                  placeholder="Order Number"
                  value={formik.values.order_no}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.order_no && formik.touched.order_no
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.order_no}
                </Form.Control.Feedback>
              </div>
            </Col>
            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>
                  Dispatch No. <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="dispatch_no"
                  placeholder="Dispatch Number"
                  value={formik.values.dispatch_no}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.dispatch_no && formik.touched.dispatch_no
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.dispatch_no}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>
                  Received By <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={userOptions}
                  getOptionLabel={(option: any) =>
                    option?.first_name && option?.last_name
                      ? `${option.first_name} ${option.last_name}`
                      : option?.username || option?.email || ""
                  }
                  value={
                    userOptions.find(
                      (opt: any) =>
                        opt.user_id === formik.values.received_by_user_id
                    ) || null
                  }
                  onChange={(event, selectedOption: any) => {
                    formik.setFieldValue(
                      "received_by_user_id",
                      selectedOption?.user_id || ""
                    );
                    formik.setFieldValue(
                      "received_by",
                      selectedOption?.first_name && selectedOption?.last_name
                        ? `${selectedOption.first_name} ${selectedOption.last_name}`
                        : selectedOption?.username || ""
                    );
                  }}
                  onBlur={() =>
                    formik.setFieldTouched("received_by_user_id", true)
                  }
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select User"
                      name="received_by"
                      fullWidth
                      size="small"
                      error={
                        formik.touched.received_by_user_id &&
                        Boolean(formik.errors.received_by_user_id)
                      }
                      helperText={
                        formik.touched.received_by_user_id &&
                        typeof formik.errors.received_by_user_id === "string"
                          ? formik.errors.received_by_user_id
                          : undefined
                      }
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor:
                              formik.touched.received_by_user_id &&
                              formik.errors.received_by_user_id
                                ? "#dc3545"
                                : "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor:
                              formik.touched.received_by_user_id &&
                              formik.errors.received_by_user_id
                                ? "#dc3545"
                                : "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor:
                              formik.touched.received_by_user_id &&
                              formik.errors.received_by_user_id
                                ? "#dc3545"
                                : "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        opt?.first_name && opt?.last_name
                          ? `${opt.first_name} ${opt.last_name}`
                          : opt?.username || opt?.email || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor:
                          formik.touched.received_by_user_id &&
                          formik.errors.received_by_user_id
                            ? "#dc3545"
                            : "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor:
                          formik.touched.received_by_user_id &&
                          formik.errors.received_by_user_id
                            ? "#dc3545"
                            : "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor:
                          formik.touched.received_by_user_id &&
                          formik.errors.received_by_user_id
                            ? "#dc3545"
                            : "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>
            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  type="text"
                  name="remark"
                  placeholder="Enter Remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </Col>

            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>Upload document</Form.Label>

                {/* Custom styled box */}
                <div
                  className={`d-flex align-items-center justify-content-between p-2 border rounded ${
                    formik.errors.upload_document &&
                    formik.touched.upload_document
                      ? "border-danger"
                      : ""
                  }`}
                  style={{
                    height: "40px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: "#fff",
                    borderColor:
                      formik.errors.upload_document &&
                      formik.touched.upload_document
                        ? "#dc3545"
                        : "#ced4da",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#80bdff";
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#ced4da";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                  onClick={() => {
                    document.getElementById("uploadDocInput")?.click();
                  }}
                >
                  <span
                    className="text-muted"
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginRight: "8px",
                      minWidth: 0,
                    }}
                    title={
                      formik.values.upload_document instanceof File
                        ? formik.values.upload_document.name
                        : "Click to upload document"
                    }
                  >
                    {formik.values.upload_document instanceof File
                      ? formik.values.upload_document.name
                      : "Click to upload document"}
                  </span>

                  {/* Action buttons */}
                  <div
                    className="d-flex align-items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                    style={{ flexShrink: 0 }}
                  >
                    {formik.values.upload_document instanceof File ? (
                      <>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("uploadDocInput")?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : (
                      <i
                        className="bi bi-upload"
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          color: "#6c757d",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#007bff";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#6c757d";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                    )}
                  </div>
                </div>

                {/* Hidden real file input */}
                <Form.Control
                  id="uploadDocInput"
                  type="file"
                  name="upload_document"
                  style={{ display: "none" }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const file = event.currentTarget.files?.[0] || null;
                    formik.setFieldValue("upload_document", file);
                    formik.setFieldTouched("upload_document", true);
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.upload_document &&
                    formik.touched.upload_document
                  }
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.upload_document}
                </Form.Control.Feedback>
              </div>
            </Col>
          </Row>

          <hr />

          <Row className="align-items-center mb-4">
            <Col>
              <h6 className="card-title mb-0">Items Details</h6>
            </Col>
          </Row>

          <style>
            {`
                      .sticky-column {
                        position: relative;
                      }
                      .sticky-column::before {
                        content: '';
                        position: absolute;
                        left: 0;
                        top: 0;
                        bottom: 0;
                        width: 1px;
                        background-color: #dee2e6;
                        z-index: 12;
                      }
                      thead .sticky-column::before {
                        z-index: 13;
                      }
                    `}
          </style>
          <Table hover responsive className="align-middle text-center">
            <thead>
              <tr>
                <th style={{ ...tableStyles.headerAlt, minWidth: "80px" }}>
                  Sr No.
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Category
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Sub Category
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Item Name
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>
                  Item Code
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>
                  Unit
                </th>

                <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
                  Order Quantity
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "180px" }}>
                  Dispatched Quantity
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "180px" }}>
                  Return Quantity
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
                  Rate
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
                  Amount
                </th>

                <th
                  style={{
                    ...tableStyles.headerAlt,
                    minWidth: "200px",
                    maxWidth: "250px",
                  }}
                >
                  Remarks
                </th>
              </tr>
            </thead>

            <tbody>
              {/* Existing Rows */}
              {formik?.values?.grn_items?.map((row: any, idx: number) => {
                return (
                  <tr key={idx}>
                    <td style={tableStyles.cell}>{idx + 1}</td>

                    {/* Category */}
                    <td style={tableStyles.cell}>
                      <span>{row?.category_name}</span>
                    </td>
                    <td style={tableStyles.cell}>
                      <span>{row?.sub_category_name}</span>
                    </td>

                    {/* Item Name */}
                    <td style={tableStyles.cell}>
                      <span>
                        {row?.item_details?.item_name || row?.item_name || ""}
                      </span>
                    </td>

                    {/* Item Code */}
                    <td style={tableStyles.cell}>
                      <span>
                        {row?.item_details?.item_code || row?.item_code || ""}
                      </span>
                    </td>
                    {/* Item Unit */}
                    <td style={tableStyles.cell}>
                      <span>{row.unit_name}</span>
                    </td>

                    <td style={tableStyles.cell}>
                      <span>{row.order_quantity || 0}</span>
                    </td>
                    <td style={tableStyles.cell}>
                      <TextField
                        size="small"
                        type="text"
                        disabled
                        placeholder="Enter dispatch quantity"
                        value={
                          row?.dispatch_quantity !== undefined &&
                          row?.dispatch_quantity !== null
                            ? String(row.dispatch_quantity)
                            : ""
                        }
                        onChange={(e) => {
                          const validatedValue = handleNumberInput(e);
                          if (validatedValue !== false) {
                            // Store as string while typing for better UX
                            handleItemFieldChange(
                              idx,
                              "dispatch_quantity",
                              validatedValue
                            );
                          }
                        }}
                        onBlur={(e) => {
                          // Convert to number on blur
                          const numValue =
                            e.target.value === ""
                              ? 0
                              : parseFloat(e.target.value) || 0;
                          handleItemFieldChange(
                            idx,
                            "dispatch_quantity",
                            numValue
                          );
                          formik.setFieldTouched(
                            `grn_items.${idx}.dispatch_quantity`,
                            true
                          );
                        }}
                        inputProps={{
                          pattern: "[0-9]*\\.?[0-9]*",
                          inputMode: "numeric",
                        }}
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        }}
                      />
                    </td>

                    <td style={tableStyles.cell}>
                      <TextField
                        size="small"
                        type="text"
                        placeholder="Enter return quantity"
                        disabled={true}
                        value={
                          row?.return_quantity !== undefined &&
                          row?.return_quantity !== null
                            ? String(row.return_quantity)
                            : ""
                        }
                        onChange={(e) => {
                          const validatedValue = handleNumberInput(e);
                          if (validatedValue !== false) {
                            // Store as string while typing for better UX
                            handleItemFieldChange(
                              idx,
                              "return_quantity",
                              validatedValue
                            );
                          }
                        }}
                        onBlur={(e) => {
                          // Convert to number on blur
                          const numValue =
                            e.target.value === ""
                              ? 0
                              : parseFloat(e.target.value) || 0;
                          handleItemFieldChange(
                            idx,
                            "return_quantity",
                            numValue
                          );
                          // Calculate amount on blur
                          const rate =
                            typeof row?.rate === "string"
                              ? parseFloat(row.rate) || 0
                              : row?.rate || 0;
                          handleAmountCalculation(idx, numValue, rate);
                          formik.setFieldTouched(
                            `grn_items.${idx}.return_quantity`,
                            true
                          );
                        }}
                        error={
                          Boolean(
                            Array.isArray(formik.touched.grn_items) &&
                              formik.touched.grn_items[idx] &&
                              (formik.touched.grn_items[idx] as any)
                                ?.return_quantity
                          ) &&
                          (parseFloat(row?.return_quantity || 0) <= 0 ||
                            parseFloat(row?.return_quantity || 0) >
                              parseFloat(row?.dispatch_quantity || 0))
                        }
                        helperText={
                          Boolean(
                            Array.isArray(formik.touched.grn_items) &&
                              formik.touched.grn_items[idx] &&
                              (formik.touched.grn_items[idx] as any)
                                ?.return_quantity
                          )
                            ? parseFloat(row?.return_quantity || 0) <= 0
                              ? "Return quantity must be greater than zero"
                              : parseFloat(row?.return_quantity || 0) >
                                parseFloat(row?.dispatch_quantity || 0)
                              ? "Return quantity cannot be greater than dispatched quantity"
                              : undefined
                            : undefined
                        }
                        inputProps={{
                          pattern: "[0-9]*\\.?[0-9]*",
                          inputMode: "numeric",
                        }}
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        }}
                      />
                    </td>
                    <td style={tableStyles.cell}>
                      <TextField
                        size="small"
                        type="text"
                        placeholder="Enter rate"
                        value={
                          row?.rate !== undefined && row?.rate !== null
                            ? String(row.rate)
                            : ""
                        }
                        onChange={(e) => {
                          const validatedValue = handleNumberInput(e);
                          if (validatedValue !== false) {
                            // Store as string while typing for better UX
                            handleItemFieldChange(idx, "rate", validatedValue);
                          }
                        }}
                        onBlur={(e) => {
                          // Convert to number on blur
                          const numValue =
                            e.target.value === ""
                              ? 0
                              : parseFloat(e.target.value) || 0;
                          handleItemFieldChange(idx, "rate", numValue);
                          // Calculate amount on blur
                          const returnQty =
                            typeof row?.return_quantity === "string"
                              ? parseFloat(row.return_quantity) || 0
                              : row?.return_quantity || 0;
                          handleAmountCalculation(idx, returnQty, numValue);
                          formik.setFieldTouched(`grn_items.${idx}.rate`, true);
                        }}
                        inputProps={{
                          pattern: "[0-9]*\\.?[0-9]*",
                          inputMode: "numeric",
                        }}
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        }}
                      />
                    </td>
                    <td style={tableStyles.cell}>
                      <span>{row?.amount || 0}</span>
                    </td>

                    {/* Remarks */}
                    <td
                      style={{
                        ...tableStyles.cell,
                        maxWidth: "250px",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                      }}
                    >
                      <TextField
                        size="small"
                        type="text"
                        placeholder="Enter remark"
                        value={row?.remarks || ""}
                        onChange={(e) => {
                          handleItemFieldChange(idx, "remarks", e.target.value);
                        }}
                        onBlur={() => {
                          formik.setFieldTouched(
                            `grn_items.${idx}.remarks`,
                            true
                          );
                        }}
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton
              label="Cancel"
              onClick={() => {
                showConfirm(
                  "Any unsaved changes will be lost.",
                  "Are you sure?",
                  "Yes",
                  "No"
                ).then((result) => {
                  if (result.isConfirmed) {
                    navigate("/warehouse-item-inward");
                  }
                });
              }}
            />

            <SubmitButton
              isSubmitting={formik.isSubmitting}
              labels={{
                create: "Create GRN",
                submitting: "Creating GRN",
              }}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default SalesOrderReturnGNRForm;
